"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { View, StyleSheet, TouchableOpacity } from "react-native"
import { Button, Text, useTheme } from "react-native-paper"
import { Layers, ChevronDown } from "lucide-react"

interface MapTypeControlProps {
  onChange: (type: string) => void
  currentType: string
}

export const MapTypeControl: React.FC<MapTypeControlProps> = ({ onChange, currentType }) => {
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<View>(null)

  const mapTypes = [
    { id: "roadmap", label: "Road Map" },
    { id: "satellite", label: "Satellite" },
    { id: "hybrid", label: "Hybrid" },
    { id: "terrain", label: "Terrain" },
  ]

  const getCurrentTypeLabel = () => {
    const type = mapTypes.find((type) => type.id === currentType)
    return type ? type.label : "Road Map"
  }

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        // @ts-ignore - _nativeTag exists on View in React Native Web
        containerRef.current._nativeTag &&
        // @ts-ignore - contains is a DOM method
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  return (
    <View ref={containerRef} style={[styles.container, { backgroundColor: theme.colors.elevation.level2 }]}>
      <Button
        mode="contained"
        onPress={() => setIsOpen(!isOpen)}
        style={styles.button}
        contentStyle={styles.buttonContent}
      >
        <View style={styles.buttonInner}>
          <Layers size={18} />
          <Text style={styles.buttonText}>{getCurrentTypeLabel()}</Text>
          <ChevronDown size={16} />
        </View>
      </Button>

      {isOpen && (
        <View style={[styles.dropdown, { backgroundColor: theme.colors.elevation.level3 }]}>
          {mapTypes.map((type) => (
            <TouchableOpacity
              key={type.id}
              style={[
                styles.dropdownItem,
                currentType === type.id && { backgroundColor: theme.colors.primaryContainer },
              ]}
              onPress={() => {
                onChange(type.id)
                setIsOpen(false)
              }}
            >
              <Text
                style={[
                  styles.dropdownItemText,
                  { color: theme.colors.onSurface },
                  currentType === type.id && { color: theme.colors.primary, fontWeight: "500" },
                ]}
              >
                {type.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    top: 10,
    left: 10, // Changed from right: 10 to left: 10
    borderRadius: 4,
    zIndex: 1000,
  },
  button: {
    borderRadius: 4,
    height: 36,
    minWidth: 120,
    paddingHorizontal: 0,
  },
  buttonContent: {
    height: "100%",
    padding: 0,
  },
  buttonInner: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    width: "100%",
    gap: 8,
  },
  buttonText: {
    fontSize: 14,
    flex: 1,
  },
  dropdown: {
    position: "absolute",
    top: 40,
    right: 0,
    width: 150,
    borderRadius: 4,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
    zIndex: 1001,
  },
  dropdownItem: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  dropdownItemText: {
    fontSize: 14,
  },
})

